import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import RadarIcon from '@mui/icons-material/Radar';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PageHeader from '../components/common/PageHeader';
import { NmapScanOptions, NmapScanResult, workbenchService } from '../services/workbenchService';

type NmapPreset = 'quick' | 'full' | 'vuln' | 'custom';
type ScanTechnique = NmapScanOptions['scan_technique'];
type TimingMark = { value: number; label: string };

const TIMING_MARKS: TimingMark[] = [
  { value: 0, label: 'T0' },
  { value: 1, label: 'T1' },
  { value: 2, label: 'T2' },
  { value: 3, label: 'T3' },
  { value: 4, label: 'T4' },
  { value: 5, label: 'T5' },
];

const TIMING_DESCRIPTIONS: Record<number, string> = {
  0: 'Paranoid - sehr langsam, für IDS-Umgehung',
  1: 'Sneaky - langsam, reduziert Erkennungsrisiko',
  2: 'Polite - bandbreitenschonend',
  3: 'Normal - Standardmodus',
  4: 'Aggressive - schnell, gutes Netz vorausgesetzt',
  5: 'Insane - maximale Geschwindigkeit, fehleranfällig',
};

const PRESET_CONFIG: Record<NmapPreset, Omit<NmapScanOptions, 'target'>> = {
  quick: {
    scan_type: 'quick',
    scan_technique: 'syn',
    timing: 4,
    ports: '',
    aggressive: false,
    service_version: false,
    os_detection: false,
    default_scripts: false,
    traceroute: false,
    no_ping: false,
    verbose: false,
  },
  full: {
    scan_type: 'full',
    scan_technique: 'syn',
    timing: 4,
    ports: '',
    aggressive: false,
    service_version: true,
    os_detection: false,
    default_scripts: false,
    traceroute: false,
    no_ping: false,
    verbose: false,
  },
  vuln: {
    scan_type: 'vuln',
    scan_technique: 'syn',
    timing: 4,
    ports: '',
    aggressive: false,
    service_version: false,
    os_detection: false,
    default_scripts: true,
    traceroute: false,
    no_ping: false,
    verbose: false,
  },
  custom: {
    scan_type: 'custom',
    scan_technique: 'syn',
    timing: 3,
    ports: '',
    aggressive: false,
    service_version: false,
    os_detection: false,
    default_scripts: false,
    traceroute: false,
    no_ping: false,
    verbose: false,
  },
};

const WorkbenchNmapPage: React.FC = () => {
  const theme = useTheme();
  const [target, setTarget] = useState('');
  const [preset, setPreset] = useState<NmapPreset>('quick');
  const [scanOptions, setScanOptions] = useState<Omit<NmapScanOptions, 'target'>>(PRESET_CONFIG.quick);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<NmapScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const canScan = target.trim().length > 0 && !loading;

  const applyPreset = (nextPreset: NmapPreset) => {
    setPreset(nextPreset);
    setScanOptions(PRESET_CONFIG[nextPreset]);
  };

  const switchToCustomIfNeeded = () => {
    if (preset !== 'custom') {
      setPreset('custom');
    }
  };

  const updateOption = <K extends keyof Omit<NmapScanOptions, 'target'>>(key: K, value: Omit<NmapScanOptions, 'target'>[K]) => {
    switchToCustomIfNeeded();
    setScanOptions((prev) => {
      const next = { ...prev, [key]: value, scan_type: 'custom' };
      if (key === 'aggressive' && value === true) {
        next.service_version = true;
        next.os_detection = true;
        next.default_scripts = true;
        next.traceroute = true;
      }
      return next;
    });
  };

  const handleScan = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const requestBody: NmapScanOptions = {
        target: target.trim(),
        ...scanOptions,
        scan_type: preset,
      };
      const data = await workbenchService.nmapScan(requestBody);
      setResult(data);
    } catch {
      setError('Scan fehlgeschlagen. Bitte Parameter prüfen und erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.output) return;
    try {
      await navigator.clipboard.writeText(result.output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard might be blocked
    }
  };

  const renderOptionSwitch = (
    label: string,
    hint: string,
    key: keyof Pick<NmapScanOptions, 'aggressive' | 'service_version' | 'os_detection' | 'default_scripts' | 'traceroute' | 'no_ping' | 'verbose'>,
    disabled = false,
  ) => (
    <Box sx={{ minWidth: 240, flex: 1 }}>
      <FormControlLabel
        control={(
          <Switch
            checked={scanOptions[key]}
            onChange={(e) => updateOption(key, e.target.checked)}
            disabled={disabled}
          />
        )}
        label={label}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
        {hint}
      </Typography>
    </Box>
  );

  const aggressiveManaged = scanOptions.aggressive;
  const timingDescription = TIMING_DESCRIPTIONS[scanOptions.timing] || TIMING_DESCRIPTIONS[3];

  return (
    <Box>
      <PageHeader
        title="Nmap Scanner"
        subtitle="Netzwerk-Scanner für Port-Scans, Service-Erkennung und Schwachstellen-Checks"
        breadcrumbs={[
          { label: 'Workbench', path: '/workbench' },
          { label: 'Nmap Scanner' },
        ]}
      />

      <Paper sx={{ p: 3, borderRadius: 2, maxWidth: 980, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Target"
            placeholder="z.B. 192.168.1.1 oder example.com"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            fullWidth
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Preset</InputLabel>
            <Select
              value={preset}
              label="Preset"
              onChange={(e) => applyPreset(e.target.value as NmapPreset)}
            >
              <MenuItem value="quick">Quick Scan (-T4 -F)</MenuItem>
              <MenuItem value="full">Full Scan (-T4 -p- -sV)</MenuItem>
              <MenuItem value="vuln">Vuln Scan (-T4 --script vuln)</MenuItem>
              <MenuItem value="custom">Custom (frei konfigurierbar)</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Accordion expanded={advancedOpen} onChange={(_, expanded) => setAdvancedOpen(expanded)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600 }}>Erweiterte Optionen</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 320, flex: 1 }}>
                  <InputLabel>Scan-Technik</InputLabel>
                  <Select
                    value={scanOptions.scan_technique}
                    label="Scan-Technik"
                    onChange={(e) => updateOption('scan_technique', e.target.value as ScanTechnique)}
                  >
                    <MenuItem value="syn">SYN Scan (-sS) - schnell, stealthy, Standard</MenuItem>
                    <MenuItem value="connect">TCP Connect (-sT) - ohne Root, langsamer</MenuItem>
                    <MenuItem value="udp">UDP Scan (-sU) - UDP Ports, langsam</MenuItem>
                    <MenuItem value="syn_udp">SYN + UDP (-sS -sU) - beide Protokolle</MenuItem>
                    <MenuItem value="ack">ACK Scan (-sA) - Firewall-Regeln erkennen</MenuItem>
                    <MenuItem value="null">NULL Scan (-sN) - Filter-Bypass-Check</MenuItem>
                    <MenuItem value="fin">FIN Scan (-sF) - Filter-Bypass-Check</MenuItem>
                    <MenuItem value="xmas">Xmas Scan (-sX) - Filter-Bypass-Check</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ flex: 1, px: { xs: 1, md: 2 } }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Timing-Level (-T{scanOptions.timing})
                  </Typography>
                  <Slider
                    min={0}
                    max={5}
                    step={1}
                    marks={TIMING_MARKS}
                    valueLabelDisplay="on"
                    value={scanOptions.timing}
                    onChange={(_, value) => updateOption('timing', value as number)}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {timingDescription}
                  </Typography>
                </Box>
              </Stack>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Wichtige Nmap-Optionen
                </Typography>
                <FormGroup row sx={{ gap: 1 }}>
                  {renderOptionSwitch('-A (Aggressive)', 'OS-Erkennung, Version, Scripts und Traceroute kombiniert', 'aggressive')}
                  {renderOptionSwitch('-sV (Service Version)', 'Offene Dienste und Versionen identifizieren', 'service_version', aggressiveManaged)}
                  {renderOptionSwitch('-O (OS Detection)', 'Betriebssystem-Erkennung aktivieren', 'os_detection', aggressiveManaged)}
                  {renderOptionSwitch('-sC (Default Scripts)', 'NSE Standard-Scripts ausführen', 'default_scripts', aggressiveManaged)}
                  {renderOptionSwitch('--traceroute', 'Netzwerk-Pfad zum Ziel ausgeben', 'traceroute', aggressiveManaged)}
                  {renderOptionSwitch('-Pn (No Ping)', 'Host als online annehmen und Ping überspringen', 'no_ping')}
                  {renderOptionSwitch('-v (Verbose)', 'Ausführlichere Ausgabe für Debugging', 'verbose')}
                </FormGroup>
                {aggressiveManaged && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    Bei aktiviertem -A werden -sV, -O, -sC und --traceroute automatisch gesetzt.
                  </Alert>
                )}
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            label="Ports (optional)"
            placeholder="z.B. 80,443 oder 1-1000"
            value={scanOptions.ports || ''}
            onChange={(e) => updateOption('ports', e.target.value)}
            size="small"
            sx={{ minWidth: 260, flex: 1 }}
          />

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RadarIcon />}
            disabled={!canScan}
            onClick={handleScan}
            sx={{ fontWeight: 700, px: 4, minWidth: 180 }}
          >
            {loading ? 'Scanne...' : 'Scan starten'}
          </Button>
        </Stack>

        {error && <Typography color="error" variant="body2">{error}</Typography>}
      </Paper>

      {result && (
        <Paper
          sx={{
            mt: 3,
            p: 0,
            borderRadius: 2,
            maxWidth: 980,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 1,
              bgcolor: result.success
                ? `${theme.palette.success.main}18`
                : `${theme.palette.error.main}18`,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {result.success ? 'Scan erfolgreich' : 'Scan fehlgeschlagen'} - {result.target} ({result.scan_type})
            </Typography>
            <Tooltip title={copied ? 'Kopiert!' : 'Output kopieren'}>
              <IconButton size="small" onClick={handleCopy}>
                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              p: 2,
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              fontSize: '0.8rem',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              maxHeight: 600,
              overflowY: 'auto',
              bgcolor: theme.palette.mode === 'dark' ? '#0d1117' : '#f6f8fa',
              color: theme.palette.mode === 'dark' ? '#c9d1d9' : '#24292f',
            }}
          >
            {result.output || 'Kein Output erhalten.'}
          </Box>
        </Paper>
      )}

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="In Zwischenablage kopiert"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default WorkbenchNmapPage;
